import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Lock, User } from "lucide-react";
import { useForm } from "react-hook-form";

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
} from "@pbz/ui-kit";

import {
  useEmailOtpSignIn,
  useSendVerificationOtp,
} from "@/hooks/useAuthMutations";
import { type LoginFormValues, loginSchema } from "@/schema/auth";
import { HOME_ROUTE, REGEX } from "@/utils/constants";

const emailOnlySchema = loginSchema.pick({ email: true });

const LoginPage: React.FC = () => {
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const navigate = useNavigate();

  const sendOtpMutation = useSendVerificationOtp();
  const signInMutation = useEmailOtpSignIn();

  const form = useForm<LoginFormValues>({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      otp: "",
    },
  });

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }
    const timerId = setTimeout(() => {
      setResendCooldown(resendCooldown - 1);
    }, 1000);
    return () => clearTimeout(timerId);
  }, [resendCooldown]);

  const handleGetOtp = async () => {
    const isIdentifierValid = await form.trigger("email");
    if (!isIdentifierValid) {
      return;
    }

    const email = form.getValues("email");
    const result = emailOnlySchema.safeParse({ email });
    if (!result.success) {
      form.setError("email", {
        type: "manual",
        message: result.error.message || "Invalid email",
      });
      return;
    }

    sendOtpMutation.mutate(
      { email, type: "sign-in" },
      {
        onSuccess: () => {
          setOtpSent(true);
          setResendCooldown(30);
          form.setFocus("otp");
        },
      },
    );
  };

  // biome-ignore lint/suspicious/useAwait: <fix>
  const handleResendOtp = async () => {
    if (sendOtpMutation.isPending || resendCooldown > 0) {
      return;
    }
    const email = form.getValues("email");

    sendOtpMutation.mutate(
      { email, type: "sign-in" },
      {
        onSuccess: () => {
          setResendCooldown(30);
          form.setValue("otp", "");
          form.clearErrors("otp");
          form.setFocus("otp");
        },
      },
    );
  };

  // biome-ignore lint/suspicious/useAwait: <fix>
  const handleLogin = async (payload: LoginFormValues) => {
    if (!payload.otp) {
      form.setError("otp", { message: "OTP is required" });
      return;
    }

    const isOtpValid = REGEX.OTP.test(payload.otp ?? "");
    if (!isOtpValid) {
      form.setError("otp", {
        type: "manual",
        message: "Please enter a valid 6-digit OTP.",
      });
      return;
    }

    const result = loginSchema.safeParse(payload);
    if (!result.success) {
      return;
    }

    signInMutation.mutate(
      {
        email: payload.email,
        otp: payload.otp,
      },
      {
        onSuccess: () => {
          // Reset state on successful login
          form.reset();
          setResendCooldown(0);
          setOtpSent(false);
          navigate({ to: HOME_ROUTE, replace: true });
        },
      },
    );
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="flex w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Left image panel */}
        <div className="hidden w-1/2 flex-col items-center justify-center bg-gray-100 p-8 md:flex">
          <img
            alt="A modern credit card with a sleek design, representing financial partnerships."
            className="max-h-80 object-contain"
            src="https://cdn.paisabazaar.ae/pbuae/posp/images/banner-posp-partner-login.svg"
          />
        </div>
        {/* Right login panel */}
        <div className="flex w-full flex-col items-center justify-center bg-white p-8 md:w-1/2 md:p-12">
          <div className="w-full max-w-sm">
            <img
              alt="Paisabazaar Logo"
              className="mb-5 ml-15 h-9 w-auto"
              src="https://cdn.paisabazaar.ae/common/images/logo.svg"
            />
            <h1 className="mb-2 text-center font-bold text-2xl text-gray-900">
              Login to your account!
            </h1>
            <p className="mb-8 text-center text-gray-600 text-sm">
              {otpSent
                ? `An OTP has been sent to ${form.getValues("email")}`
                : "Enter your credentials to receive an OTP."}
            </p>
            <Form {...form}>
              <form
                className="space-y-6"
                onSubmit={form.handleSubmit(handleLogin)}
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <div className="relative">
                        <User className=" -translate-y-1/2 absolute top-1/2 left-3 h-5 w-5 text-gray-500" />
                        <FormControl>
                          <Input
                            placeholder="Username / Email ID"
                            {...field}
                            aria-label="Username or Email ID"
                            className={`h-12 rounded-lg border bg-white text-gray-900 placeholder-gray-500 pl-10 ${
                              fieldState.error
                                ? "border-red-500 focus:border-red-500"
                                : "border-gray-300 focus:border-blue-500"
                            }`}
                            disabled={sendOtpMutation.isPending || otpSent}
                            // style={{ paddingLeft: "3rem" }}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {otpSent && (
                  <FormField
                    control={form.control}
                    name="otp"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <div className="relative">
                          <Lock className="-translate-y-1/2 absolute top-1/2 left-3 h-5 w-5 text-gray-500" />
                          <FormControl>
                            <Input
                              placeholder="Enter 6-digit OTP"
                              {...field}
                              aria-label="One-Time Password"
                              autoComplete="one-time-code"
                              className={`h-12 rounded-lg border pl-10 ${
                                fieldState.error
                                  ? "border-red-500 focus:border-red-500"
                                  : "border-gray-300 focus:border-blue-500"
                              } bg-white text-gray-900 placeholder-gray-500`}
                              disabled={signInMutation.isPending}
                              maxLength={6}
                            />
                          </FormControl>
                        </div>
                        <div className="mt-2 h-5 text-right">
                          <Button
                            className="h-auto text-sm"
                            disabled={
                              resendCooldown > 0 || sendOtpMutation.isPending
                            }
                            onClick={handleResendOtp}
                            type="button"
                            variant="link"
                          >
                            {resendCooldown > 0 ? (
                              `Resend OTP in ${resendCooldown}s`
                            ) : (
                              <span className="text-blue-500 hover:cursor-pointer">
                                Resend OTP
                              </span>
                            )}
                          </Button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                {otpSent ? (
                  <Button
                    className="h-12 w-full rounded-lg bg-orange-500 font-bold text-base hover:bg-orange-600 text-white"
                    disabled={signInMutation.isPending}
                    type="submit"
                  >
                    {signInMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Sign In
                  </Button>
                ) : (
                  <Button
                    className="h-12 w-full rounded-lg bg-orange-500 font-bold text-base hover:bg-orange-600 text-white"
                    disabled={sendOtpMutation.isPending}
                    onClick={handleGetOtp}
                    size="lg"
                    type="button"
                    variant="default"
                  >
                    {sendOtpMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Get OTP
                  </Button>
                )}
              </form>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
